/* eslint-disable complexity */
/*
* MixPanel tracking HOC
* Usage :
1) const trackedComponent = withTracking({
  page: "Your page if nay",
  contextId: <>
})(YourAtomComponent)
2) const trackedComponent = withTracking()(YourAtomComponent)
<YourAtomComponent eventActions={{
  onClick: {
  event: "Your event name",
  data: "Your event data object"  // can be object or function
  }
}}/>
*/
import { Component } from 'react';
import PropTypes from 'prop-types';
import { getBaseProperties } from '../saga';
import { withStore } from '@mindtickle/medux/Store';
import { mixpanelIdentity } from '../config';
import { isEmpty } from '@mindtickle/utils';
import mixpanelManager from '../helpers/mixpanelManager';

export const mixpanelClient = {
  track: (event, data, sendUsingBeacon = false) => {
    // eslint-disable-next-line
    console.log(
      'MIX_PANEL EVENT: ',
      event,
      ', DATA: ',
      data,
      ', sendUsingBeacon: ',
      sendUsingBeacon
    ); //eslint-disable-line
  },
};

const withTracking = (injectedProps = {}) => WrappedComponent => {
  const HOC = class HOC extends Component {
    static propTypes = {
      eventActions: PropTypes.object,
      contextId: PropTypes.string,
      store: PropTypes.object.isRequired,
    };

    constructor(props) {
      super(props);
      this.wrappedEvents = [];
    }

    componentDidMount() {
      this.wrapMethodsForFrameworkEvents();
    }

    componentDidUpdate(prevProps) {
      const { eventActions } = this.props;
      for (let e in eventActions) {
        if (this.props[e] !== prevProps[e]) {
          const eventObj = eventActions[e];
          this.createWrappedMethod(eventObj, e);
        }
      }
    }

    createWrappedMethod = (eventObj, eventAction) => {
      const { event, data: eventData } = eventObj;
      const self = this;
      const originalMethod = this.props[eventAction];
      this.wrappedEvents[eventAction] = function (...args) {
        const opData = originalMethod && originalMethod.apply(this, args);
        let trackData = eventData;
        const props = self.props; // get original props
        const state = {}; // get origin state
        if (typeof eventData === 'function') {
          trackData = eventData.apply(this, [opData, props, state, ...args]);
        }
        self.fireTracking(event, trackData);
      };
      this.setState({});
    };

    wrapMethodsForFrameworkEvents = () => {
      const { eventActions } = this.props;
      for (let e in eventActions) {
        const eventObj = eventActions[e];
        this.createWrappedMethod(eventObj, e);
      }
    };

    track = (event, mixpanelData, sendUsingBeacon = false) => {
      if (process.env.IS_DEV || process.env.isDev) {
        mixpanelClient.track(event, mixpanelData, sendUsingBeacon);
      } else if (process.env.TRACKING) {
        mixpanelManager.track(event, mixpanelData, sendUsingBeacon);
      }
    };

    // static contextTypes = { store: PropTypes.object.isRequired };

    fireTracking = (event, trackData = {}, sendUsingBeacon = false) => {
      const { event: trackEvent, contextId: _contextId, ...rest } = trackData;
      // Override event if event is runtime function of trackData
      const __event = trackEvent || event;

      // Do not track if no event presert
      if (!__event) {
        return;
      }
      const contextId = _contextId || this.props.contextId || injectedProps.contextId;
      const { trackData: injectedTrackData } = injectedProps;
      const store = this.props.store.store.getState();

      let contextData = {};
      if (contextId) {
        const contextPath = (store[contextId] && store[contextId].mixpanel) || {};
        contextData = getBaseProperties(contextPath, mixpanelIdentity.get(), store);
      }

      const mixpanelData = {
        contextData,
        ...rest,
        ...injectedTrackData,
      };
      this.track(__event, mixpanelData, sendUsingBeacon);
    };

    render() {
      const { eventActions, ...rest } = this.props; // eslint-disable-line no-unused-vars
      let trackProps = {};
      if (!eventActions || isEmpty(eventActions)) {
        trackProps.track = this.fireTracking;
      }
      return <WrappedComponent {...rest} {...this.wrappedEvents} {...trackProps} />;
    }
  };

  return withStore(HOC);
};

export default withTracking;
